// Venue facilities: the options a venue picks from, and the conversion between the
// form's flat fields and the API's `facilities` shape (backend VenueFacilities).
// Values match the backend enums in server.py.

export const FOOD_SOURCE_OPTIONS = [
  { value: 'at_venue', label: 'Served at the venue' },
  { value: 'voucher', label: 'Voucher or buyout' },
  { value: 'nearby', label: 'Nothing served, food nearby' },
  { value: 'none', label: 'No food' },
];

export const DIET_OPTIONS = [
  { value: 'available', label: 'Yes' },
  { value: 'unavailable', label: 'No' },
  { value: 'ask', label: 'Ask ahead' },
];

export const DRESSING_ROOM_OPTIONS = [
  { value: 'none', label: 'None' },
  { value: 'shared', label: 'Shared' },
  { value: 'private', label: 'Private' },
];

export const GREEN_ROOM_OPTIONS = [
  { value: 'no', label: 'None' },
  { value: 'shared', label: 'Shared' },
  { value: 'private', label: 'Private' },
];

export const ARTIST_TOILET_OPTIONS = [
  { value: 'separate', label: 'Separate from the public' },
  { value: 'shared_with_public', label: 'Shared with the public' },
];

export const SHOWER_OPTIONS = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
];

// Whether the talent gets drink tokens; the API stores it as true/false. No count yet.
export const DRINK_TOKEN_OPTIONS = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
];

export const STORAGE_OPTIONS = [
  { value: 'none', label: 'None' },
  { value: 'lockable_room', label: 'Lockable room' },
  { value: 'safe', label: 'Safe' },
  { value: 'venue_supervised', label: 'Venue supervised' },
];

export const WIFI_OPTIONS = [
  { value: 'guest_network', label: 'Guest network' },
  { value: 'staff_network', label: 'Staff network' },
  { value: 'none', label: 'None' },
  { value: 'unknown', label: 'Unknown' },
];

export const EMPTY_FACILITIES_FORM = {
  foodSource: '',
  servedAt: '',
  vegetarian: '',
  vegan: '',
  nearbyNote: '',
  nearbyPlace: '',
  nearbyWalkMinutes: '',
  nearbyOpenUntil: '',
  dressingRoom: '',
  greenRoom: '',
  artistToilet: '',
  showerAccess: '',
  secureStorage: '',
  wifi: '',
  drinkTokens: '',
  guestListSpots: '',
  notes: '',
};

const text = (v) => (v == null ? '' : String(v));

export function facilitiesToForm(facilities) {
  const h = facilities?.hospitality || {};
  const c = facilities?.catering || {};
  const n = c.nearby || {};
  return {
    foodSource: text(c.provided),
    servedAt: text(c.served_at),
    vegetarian: text(c.vegetarian),
    vegan: text(c.vegan),
    nearbyNote: text(n.note),
    nearbyPlace: text(n.place),
    nearbyWalkMinutes: text(n.walk_minutes),
    nearbyOpenUntil: text(n.open_until),
    dressingRoom: text(h.dressingRoom),
    greenRoom: text(h.greenRoom),
    artistToilet: text(h.artistToilet),
    showerAccess: text(h.showerAccess),
    secureStorage: text(h.secureStorage),
    wifi: text(h.wifi),
    drinkTokens: h.drinkTokens === true ? 'yes' : h.drinkTokens === false ? 'no' : '',
    guestListSpots: text(h.guestListSpots),
    notes: text(h.notes),
  };
}

const orNull = (v) => (v && String(v).trim() ? String(v).trim() : null);

const count = (v, max) => {
  if (v === '' || v == null) return null;
  const n = parseInt(v, 10);
  return Number.isNaN(n) ? null : Math.min(Math.max(n, 0), max);
};

export function formToFacilities(form) {
  const source = form.foodSource;
  const catering = source
    ? {
        provided: source,
        served_at: source === 'at_venue' ? orNull(form.servedAt) : null,
        // Diet describes food the talent can get: the venue's meal, or the place nearby.
        vegetarian: ['at_venue', 'nearby'].includes(source) ? orNull(form.vegetarian) : null,
        vegan: ['at_venue', 'nearby'].includes(source) ? orNull(form.vegan) : null,
        nearby: source === 'nearby'
          ? {
              note: orNull(form.nearbyNote),
              place: orNull(form.nearbyPlace),
              walk_minutes: count(form.nearbyWalkMinutes, 60),
              open_until: orNull(form.nearbyOpenUntil),
            }
          : null,
      }
    : null;

  return {
    hospitality: {
      dressingRoom: orNull(form.dressingRoom),
      greenRoom: orNull(form.greenRoom),
      artistToilet: orNull(form.artistToilet),
      showerAccess: orNull(form.showerAccess),
      secureStorage: orNull(form.secureStorage),
      wifi: orNull(form.wifi),
      drinkTokens: form.drinkTokens === 'yes' ? true : form.drinkTokens === 'no' ? false : null,
      guestListSpots: count(form.guestListSpots, 50),
      notes: orNull(form.notes),
    },
    catering,
  };
}
